// src/components/watchlist/LocationMap.js
import React, { useRef, useState, useEffect } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  Polygon,
  Marker,
  Popup,
  useMap,
} from 'react-leaflet'
import { EditControl } from 'react-leaflet-draw'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import { useTranslation } from 'react-i18next'

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

// Helper component to update map view
const MapUpdater = ({ center, zoom = 12 }) => {
  const map = useMap()

  useEffect(() => {
    if (center && Array.isArray(center) && center.length === 2) {
      map.setView(center, zoom)
    }
  }, [center, zoom, map])

  return null
}

// Main component
const LocationMap = ({
  onLocationSelected,
  readOnly = false,
  initialLocation = null,
  center = [52.3676, 4.9041],
  zoom = 12,
  onCenterCalculated = null,
}) => {
  const { t } = useTranslation()
  const featureGroupRef = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [initialShapesDrawn, setInitialShapesDrawn] = useState(false)

  // For polygon visualization only
  const [polygonCoords, setPolygonCoords] = useState(null)
  const [markerPosition, setMarkerPosition] = useState(null)

  // If in readonly mode and we have an initial location, set up visualization
  useEffect(() => {
    if (readOnly && initialLocation && !initialShapesDrawn) {
      if (initialLocation.type === 'polygon' && initialLocation.properties?.coordinates) {
        // Use coordinates directly since they're already in [lat, lng] format
        const coords = initialLocation.properties.coordinates
        setPolygonCoords(coords)

        // Calculate polygon center for map centering
        if (coords.length > 0) {
          const calculatePolygonCenter = (coordinates) => {
            // Calculate the centroid of the polygon
            let sumLat = 0
            let sumLng = 0

            for (let i = 0; i < coordinates.length; i++) {
              sumLat += coordinates[i][0] // latitude
              sumLng += coordinates[i][1] // longitude
            }

            return [sumLat / coordinates.length, sumLng / coordinates.length]
          }

          const polygonCenter = calculatePolygonCenter(coords)

          // Send polygon center back to parent if callback provided
          if (onCenterCalculated) {
            onCenterCalculated(polygonCenter)
          }
        }
      } else if (initialLocation.properties?.lat && initialLocation.properties?.lon) {
        setMarkerPosition([initialLocation.properties.lat, initialLocation.properties.lon])
      }
      setInitialShapesDrawn(true)
    }
  }, [readOnly, initialLocation, initialShapesDrawn, onCenterCalculated])

  // Clear any drawn items when component unmounts or center changes
  useEffect(() => {
    return () => {
      if (featureGroupRef.current && featureGroupRef.current._layers) {
        featureGroupRef.current.clearLayers()
      }
    }
  }, [center])

  // Handle created shape
  const handleCreated = (e) => {
    const { layer, layerType } = e

    // Clear previous shapes
    if (featureGroupRef.current._layers) {
      const layerIds = Object.keys(featureGroupRef.current._layers)
      layerIds.forEach((id) => {
        if (id !== layer._leaflet_id) {
          featureGroupRef.current.removeLayer(featureGroupRef.current._layers[id])
        }
      })
    }

    if (layerType === 'polygon' || layerType === 'rectangle') {
      const { _latlngs } = layer
      const coords = _latlngs[0].map((latlng) => [latlng.lng, latlng.lat]) // GeoJSON format [lon, lat]

      onLocationSelected({
        address: '',
        country: 'Netherlands',
        postcode: '',
        osm_data: {
          type: 'polygon',
          properties: {
            coordinates: coords,
          },
        },
      })
    } else if (layerType === 'marker') {
      const { _latlng } = layer

      // For markers, reverse geocode to get the address
      reverseGeocode(_latlng.lat, _latlng.lng).then((locationData) => {
        onLocationSelected(locationData)
      })
    }
  }

  // Handle polygon editing
  const handleEdited = (e) => {
    const layers = e.layers
    layers.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        const { _latlngs } = layer
        const coords = _latlngs[0].map((latlng) => [latlng.lng, latlng.lat]) // GeoJSON format

        onLocationSelected({
          address: '',
          country: 'Netherlands',
          postcode: '',
          osm_data: {
            type: 'polygon',
            properties: {
              coordinates: coords,
            },
          },
        })
      }
    })
  }

  // Handle shape deletion
  const handleDeleted = () => {
    onLocationSelected && onLocationSelected(null)
  }

  // Reverse geocode coordinates to address
  const reverseGeocode = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`
      )
      const data = await response.json()

      if (data.error) {
        throw new Error(data.error)
      }

      return {
        address: data.display_name,
        country: data.address.country || 'Netherlands',
        postcode: data.address.postcode || '',
        osm_data: {
          type: 'point',
          properties: {
            lat: lat,
            lon: lon,
            display_name: data.display_name,
          },
        },
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error)
      // Return basic location data on error
      return {
        address: `Location at ${lat.toFixed(6)}, ${lon.toFixed(6)}`,
        country: 'Netherlands',
        postcode: '',
        osm_data: {
          type: 'point',
          properties: {
            lat: lat,
            lon: lon,
          },
        },
      }
    }
  }

  return (
    <Box sx={{ height: 400, position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
      {!mapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            bgcolor: 'rgba(255,255,255,0.8)',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        whenReady={() => setMapLoaded(true)}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapUpdater center={center} zoom={zoom} />

        {readOnly ? (
          // For read-only view, just render the shapes
          <>
            {polygonCoords && <Polygon positions={polygonCoords} color="#3388ff" />}
            {markerPosition && (
              <Marker position={markerPosition}>
                <Popup>{initialLocation.address}</Popup>
              </Marker>
            )}
          </>
        ) : (
          // For interactive view, include the drawing controls
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topright"
              onCreated={handleCreated}
              onEdited={handleEdited}
              onDeleted={handleDeleted}
              draw={{
                polyline: false,
                circle: false,
                circlemarker: false,
                marker: true,
                polygon: true,
                rectangle: true,
              }}
            />
          </FeatureGroup>
        )}
      </MapContainer>

      {!readOnly && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            right: 10,
            p: 1.5,
            bgcolor: 'rgba(255,255,255,0.9)',
            borderRadius: 1,
            boxShadow: 1,
            zIndex: 500,
          }}
        >
          <Typography variant="body2">
            {t(
              'notifications.mapInstructions',
              'Draw a shape on the map or place a marker to define your area of interest'
            )}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default LocationMap
